import React, {useState, useEffect} from 'react';
import styled from 'styled-components';

import {useModalContext} from '@bootcamp/web/src/contexts/Modal';
import {useUserDataContext} from '@bootcamp/web/src/contexts/UserData';
import {Body3, Body4, Label2} from '@bootcamp/web/src/components/Typography/next';
import {CustomInputWrapper, InputHoverWrapper} from '@bootcamp/web/src/bootcamps/pages/CreateTest/components/shared';

import DatePicker from '../DatePicker';
import {format, addDays, parse, differenceInDays} from 'date-fns';
import {ArrowRight} from '@styled-icons/fluentui-system-regular/ArrowRight';
import {
  Container,
  Header,
  Wrapper,
  HeaderText,
  CloseIcon,
  ButtonArea,
  Left,
  Right,
  TimelineComponent,
  CreateAreaText,
  CreateButton,
  StyledRocket,
  StyledWand,
  StyledSun,
  StyledTarget,
  Section,
  Heading,
  Subheading,
  RightHeading,
} from './shared';

const DefaultExamDateContainer = styled.div`
  margin-top: 16px;
`;
const StyledCustomInputWrapper = styled(CustomInputWrapper)`
  border-radius: 50%;
  border: 1px solid ${({theme, checked}) => checked ? theme.colors.brandPalette.royal.default : theme.colors.borderPalette.primary};
  background: white;
`;
const InputWrapper = styled.div`
`;
const Input = styled.input`
  margin: 0;
  opacity: 0;
  height: 0;
  width: 0;
`;
const InputActive = styled.div`
  width: 12px;
  height: 12px;
  background: ${({theme}) => theme.colors.brandPalette.royal.default};
  border-radius: 50%;
`;
const Label = styled(Label2)`
  cursor: pointer;
  margin-left: 8px;
  color: ${({theme}) => theme.colors.typographyPalette.primary};
`;
const PresetArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 12px;
  cursor: pointer;
`;
const Preset = styled.div`
  display: flex;
  flex-flow: row wrap;
  align-items: center;
  border: 1px solid ${({theme, active}) => active ? theme.colors.brandPalette.royal.default : theme.colors.borderPalette.primary};
  background: ${({theme, active}) => active ? theme.colors.brandPalette.royal.extraLight : 'transparent'};
  border-radius: 8px;
  padding: 16px;
  width: 100%;
  justify-content: space-between;
`;
const PresetName = styled(Body3)`
  color: ${({theme}) => theme.colors.typographyPalette.primary};
  font-weight: 700;
  margin-right: 12px;
`;
const PresetDuration = styled(Body3)`
  color: ${({theme}) => theme.colors.typographyPalette.secondary};
`;
const PresetDetails = styled(Body4)`
  color: ${({theme}) => theme.colors.typographyPalette.secondary};
`;
const PresetAreaWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const presetsByBootcamp = {
  'dat': [
    {name: '10 Week Schedule', duration: 70, details: 'We recommend starting with a 10 week schedule, you can always update this later!'},
  ],
  'oat': [
    {name: '10 Week Schedule', duration: 70, details: 'We recommend starting with a 10 week schedule, you can always update this later!'},
  ],
  'inbde': [
    {name: '45 Day Schedule', duration: 45},
  ],
  'med-school': [
    {name: '9 Week Schedule', duration: 63},
  ],
  'nclex': [
    {name: '2 Month Schedule', duration: 62},
  ]
}

const Radio = ({value, onChange, labelText}) => {
  return (
    <InputWrapper onClick={onChange}>
      <InputHoverWrapper active={value}>
        <StyledCustomInputWrapper htmlFor={value} checked={value}>
          <Input type={'radio'} value={value} checked={value}/>
          {value && <InputActive/>}
          </StyledCustomInputWrapper>
        <Label>{labelText}</Label>
      </InputHoverWrapper>
    </InputWrapper>
  );
};

const DefaultExamDate = ({
  onChange,
  bootcamp,
  value,
  startDate
}) => {
  const [toggled, setToggled] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState(0);

  const handleChange = (duration, index=0) => {
    const examDate = format(addDays(startDate, duration), 'yyyy-MM-dd');

    setSelectedPreset(index);
    onChange(examDate);
  };

  const handleToggle = () => {
    if (toggled) {
      setToggled(false);
      setSelectedPreset(0);
      onChange(null);
    } else {
      setToggled(true);
      setSelectedPreset(0);
      handleChange(presetsByBootcamp?.[bootcamp]?.[0]?.duration);
    }
  };

  useEffect(() => {
    if (!value) {
      setToggled(false);
      setSelectedPreset(0);
    }
  }, [value]);

  useEffect(() => {
    if (startDate >= value && toggled) {
      handleToggle();
    }
  }, [startDate]);

  return (
    <DefaultExamDateContainer>
      <Radio
        value={toggled}
        onChange={handleToggle}
        labelText={"I don't have an exam date yet."} />
      {toggled &&
      <PresetAreaWrapper>
        <PresetArea>
          {presetsByBootcamp?.[bootcamp]?.map(({name, duration}, index) => (
            <Preset key={name} active={selectedPreset === index} onClick={() => handleChange(duration, index)}>
              <PresetName>{name}</PresetName>
              <PresetDuration>Ends on {format(addDays(startDate, duration), 'MMM do, yyyy')}</PresetDuration>
            </Preset>
          ))}
        </PresetArea>
        <PresetDetails>{presetsByBootcamp?.[bootcamp]?.[0]?.details}</PresetDetails>
      </PresetAreaWrapper>
      }
    </DefaultExamDateContainer>
  );
};

function validateExamDate(examDate) {
  try {
    const parsedExamDate = parse(examDate, 'yyyy-MM-dd', new Date());

    if (parsedExamDate < new Date()) {
      return null;
    }

    return examDate;
  } catch (error) {
    return null;
  }
}

export const CreateModal = ({
  onCreate,
  onRebalance,
  totalHours=219,
  rebalance,
  startingExamDate=null,
  startingDaysOff=[],
}) => {
  const [creating, setCreating] = useState(false);
  const today = format(new Date(), 'yyyy-MM-dd');
  const [startDate, setStartDate] = useState(today);
  const [examDate, setExamDate] = useState(validateExamDate(startingExamDate));
  const [daysOff, setDaysOff] = useState(startingDaysOff);
  const [defaultExamDate, setDefaultExamDate] = useState(null);

  const {modalDispatch} = useModalContext();
  const {userModel, bootcamp} = useUserDataContext();

  const daysBetweenStartAndExam = examDate && startDate
    ? differenceInDays(parse(examDate, 'yyyy-MM-dd', new Date()), parse(startDate, 'yyyy-MM-dd', new Date())) - daysOff.length
    : null;
  const hoursPerDay = daysBetweenStartAndExam ? totalHours / daysBetweenStartAndExam : null;
  const membershipExpirationDate = new Date(userModel.membershipExpirationDate *  1000);
  const isMembershipExpirationDateBeforeExamDate = membershipExpirationDate && examDate && membershipExpirationDate < parse(examDate, 'yyyy-MM-dd', new Date());

  const handleDateChange = (value, type) => {
    if (!value) return;

    try {
      const setters = {
        start: value=> {
          // reset exam date if it's before start date
          if (examDate && value >= examDate) setExamDate(null);

          setStartDate(value);

          // reset days off if changed
          setDaysOff([]);
        },
        exam: value => {
          setExamDate(value);

          // reset days off if changed
          setDaysOff([]);

          // reset default exam date if changed
          setDefaultExamDate(null);
        },
        daysOff: setDaysOff,
      };

      setters[type](value);

    } catch (error) {

    }
  };

  const handleDefaultExamDateChange = (value) => {
    setDefaultExamDate(value);
    setExamDate(value);
  };

  const handleCreate = async () => {
    if (rebalance) {
      return onCreate(startDate, daysOff, examDate, hoursPerDay);
    }

    setCreating(true);
    await onCreate(startDate, daysOff, examDate, hoursPerDay);
    modalDispatch({type: 'close'});
  };
  const disabled = !startDate || !examDate;
  return (
    <Container id={'modal-container'} onClick={e => e.stopPropagation()}>
      <Header>
        <HeaderText>{rebalance ? 'Rebalance' : 'Create'} Study Schedule</HeaderText>
        <CloseIcon onClick={() => modalDispatch({type: 'close'})}/>
      </Header>
      <Wrapper>
        <Left>
          <Section>
            <Heading>
              {rebalance ? <StyledWand /> : <StyledRocket/>}
              {rebalance ? 'Rebalance' : ''} Start Date
            </Heading>
            <Subheading>Your study schedule will {rebalance ? 'resume' : 'begin'} on this date.</Subheading>
            <DatePicker
              min={today}
              value={startDate}
              startDate={startDate}
              onChange={event => handleDateChange(event, 'start')}
            />
          </Section>
          <Section>
            <Heading>
              <StyledTarget/>
              Exam Date
            </Heading>
            <Subheading>Your exam date can be updated anytime.</Subheading>
            <DatePicker
              startDate={startDate}
              min={format(addDays(parse(startDate, 'yyyy-MM-dd', new Date()), 1), 'yyyy-MM-dd')}
              value={examDate}
              onChange={event => handleDateChange(event, 'exam')}
            />
            {!rebalance &&
              <DefaultExamDate
                onChange={handleDefaultExamDateChange}
                value={defaultExamDate}
                startDate={startDate}
                bootcamp={bootcamp}
              />
            }
          </Section>
          <Section>
            <Heading>
              <StyledSun/>
              Days Off
            </Heading>
            <Subheading>We recommend taking one day off per week.</Subheading>
            <DatePicker
              startDate={startDate}
              min={today}
              endDate={examDate}
              onChange={event => handleDateChange(event, 'daysOff')}
              value={daysOff}
              disabled={disabled}
              placeholder={'Select days off'}
              tipText={'Tip: Click a weekday (e.g., SUN) to mark all Sundays off!'}
              multi
            />
          </Section>
        </Left>
        <Right>
          <RightHeading style={rebalance && {marginBottom: '8px'}}>Your {rebalance ? 'Rebalanced' : ''} Schedule</RightHeading>
          {rebalance && <Subheading>Rebalancing distributes all your uncompleted assignments evenly up to your exam date.</Subheading>}
            <TimelineComponent
              startDate={startDate}
              rebalance={rebalance}
              hoursPerDay={hoursPerDay}
              expirationBeforeExamDate={isMembershipExpirationDateBeforeExamDate}
              examDate={examDate}
              startingExamDate={startingExamDate}
            />
            <ButtonArea>
              <CreateButton onClick={handleCreate} loading={creating} disabled={disabled}>
                {rebalance
                  ? 'Rebalance'
                  : 'Create Study Schedule'
                }
                <ArrowRight style={{marginLeft: '8px'}} size={20} color={disabled ? '#85868e' : "white"} />
              </CreateButton>
              <CreateAreaText>You can adjust your schedule anytime</CreateAreaText>
            </ButtonArea>
        </Right>
      </Wrapper>
    </Container>
  );
};

export default CreateModal;